import { useState } from 'react';
import { useGoalsFixture } from '../../swr-config';
import { GoalsConfidenceIndicator } from './components/GoalsConfidenceIndicator';
import { ProbabilitySummaryPanel } from './panels/ProbabilitySummaryPanel';
import { TeamScoringPanel } from './panels/TeamScoringPanel';
import { ScorelineChartPanel } from './panels/ScorelineChartPanel';
import { GoalTimingPanel } from './panels/GoalTimingPanel';
import { ContextPanel } from './panels/ContextPanel';

type DetailTab = 'summary' | 'teams' | 'scorelines' | 'context';

interface GoalsFixtureDetailProps {
  fixtureId: number;
  onBack: () => void;
}

export function GoalsFixtureDetail({ fixtureId, onBack }: GoalsFixtureDetailProps) {
  const [activeTab, setActiveTab] = useState<DetailTab>('summary');
  const { data, error, isLoading } = useGoalsFixture(fixtureId);

  if (isLoading) {
    return (
      <div className="goals-fixture-detail">
        <div className="goals-loading">
          <div className="loading-spinner" />
          <p>Loading fixture analysis...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="goals-fixture-detail">
        <div className="goals-error">
          <p>Failed to load fixture data</p>
          <button onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  const formatProb = (prob: number | null) => {
    if (prob === null) return '--%';
    return `${(prob * 100).toFixed(0)}%`;
  };

  return (
    <div className="goals-fixture-detail">
      {/* Match Header Card */}
      <div className="goals-match-header">
        {/* Back Button */}
        <button className="goals-detail-back" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Overview
        </button>

        {/* Teams */}
        <div className="goals-match-teams">
          <div className="goals-match-team">
            {data.fixture.home_team.logo && (
              <img src={data.fixture.home_team.logo} alt="" className="goals-match-logo" />
            )}
            <span className="goals-match-team-name">{data.fixture.home_team.name}</span>
          </div>
          <span className="goals-match-vs">vs</span>
          <div className="goals-match-team">
            {data.fixture.away_team.logo && (
              <img src={data.fixture.away_team.logo} alt="" className="goals-match-logo" />
            )}
            <span className="goals-match-team-name">{data.fixture.away_team.name}</span>
          </div>
        </div>

        {/* Match Info */}
        <div className="goals-match-info">
          <span>
            {data.fixture.kickoff
              ? new Date(data.fixture.kickoff).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : 'TBD'}
          </span>
          {data.fixture.competition?.name && (
            <>
              <span className="goals-match-info-sep">•</span>
              <span>{data.fixture.competition.name}</span>
            </>
          )}
        </div>

        {/* Market Chips */}
        {data.prediction && (
          <div className="goals-projection-box">
            <div className="goals-projection-item">
              <span className="goals-projection-label">BTTS</span>
              <span className="goals-projection-value">{formatProb(data.prediction.btts.yes)}</span>
            </div>
            <div className="goals-projection-item">
              <span className="goals-projection-label">O2.5</span>
              <span className="goals-projection-value">{formatProb(data.prediction.over_under['2.5']?.over)}</span>
            </div>
            <div className="goals-projection-item">
              <span className="goals-projection-label">BTTS+O2.5</span>
              <span className="goals-projection-value">{formatProb(data.prediction.combos.btts_o2_5)}</span>
            </div>
          </div>
        )}

        {/* Confidence */}
        {data.confidence && (
          <div className="goals-confidence-section">
            <GoalsConfidenceIndicator
              score={data.confidence.score}
              level={data.confidence.level}
              showLabel
            />
          </div>
        )}
      </div>

      {/* Narrative */}
      {data.explainability?.narrative && (
        <div className="goals-narrative">
          <p>{data.explainability.narrative}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="goals-detail-tabs">
        {(['summary', 'teams', 'scorelines', 'context'] as DetailTab[]).map((tab) => (
          <button
            key={tab}
            className={`goals-detail-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="goals-detail-content">
        {activeTab === 'summary' && data.prediction && (
          <ProbabilitySummaryPanel prediction={data.prediction} />
        )}
        {activeTab === 'teams' && (
          <TeamScoringPanel profiles={data.team_profiles} />
        )}
        {activeTab === 'scorelines' && (
          <>
            <ScorelineChartPanel scorelines={data.scoreline_chart} />
            <GoalTimingPanel timing={data.goal_timing} />
          </>
        )}
        {activeTab === 'context' && (
          <ContextPanel context={data.context} h2h={data.h2h} referee={data.referee} />
        )}
      </div>

      {/* Key Factors */}
      {data.explainability?.key_factors && data.explainability.key_factors.length > 0 && (
        <div className="goals-key-factors">
          <h3>Key Factors</h3>
          <div className="goals-factors-list">
            {data.explainability.key_factors.map((factor, idx) => (
              <div key={idx} className={`goals-factor goals-factor-${factor.direction}`}>
                <span className="goals-factor-impact">{factor.impact}</span>
                <span className="goals-factor-detail">{factor.detail}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default GoalsFixtureDetail;
